import * as FirebaseAuth from "firebase/auth"
import { api, firestore } from "../../core"
import { useState } from "react"
import { UserDocument, UserId } from "popopo-msg"
import { Outlet, useOutletContext } from "react-router-dom"
import { useUser } from "popopo-lib-front/firestore-access"
import { useAuth } from "./useAuth"
import { LoginScreen } from "./LoginScreen"
import { AccountLinkPanel } from "./AccountLinkPanel"

export type AccessContext = {
  credential: { user: FirebaseAuth.User }
  user: UserDocument
}

export function useAccessContext() {
  return useOutletContext<AccessContext>()
}

const providerLabel = (u: FirebaseAuth.User) => {
  if (u.isAnonymous) return "匿名ユーザー"
  if (u.email) return u.email
  if (u.phoneNumber) return u.phoneNumber
  return "認証済みユーザー"
}

export const AccessGuard = () => {
  const { authState, firebaseUser, signInAnonymous, signInWithGoogle, signInWithPhone, linkWithGoogle, linkWithPhone, unlinkProvider, signOut } = useAuth()
  const [status, setStatus] = useState<undefined | "LOADING">()
  const user = useUser(firestore, firebaseUser ? UserId(firebaseUser.uid) : "loading")

  const handleRegister = async (targetUser: FirebaseAuth.User) => {
    setStatus("LOADING")
    try {
      await targetUser.getIdToken().then((idToken) =>
        api.POST("/api/v2/users", {
          headers: {
            authorization: `Bearer ${idToken}`,
          },
        }),
      )
    } catch (e) {
      console.error(e)
    } finally {
      setStatus(undefined)
    }
  }

  return (
    <>
      <div className="container-fluid py-5">
        <div className="row mb-5">
          <div className="col-12 d-flex flex-column align-items-center gap-3">
            <h1>Debug Tool</h1>

            {authState === "initial" && (
              <LoginScreen
                onSignInAnonymous={signInAnonymous}
                onSignInWithGoogle={signInWithGoogle}
                onSignInWithPhone={() => signInWithPhone("recaptcha-container")}
              />
            )}

            {authState === "loading" && <span>Signing in...</span>}

            {firebaseUser && user === "loading" && <span>Loading user...</span>}

            {firebaseUser && (
              <div>Your Firebase User ID: {firebaseUser.uid}</div>
            )}

            {firebaseUser && user === null && (
              <div className="d-flex flex-column gap-3 w-100" style={{ maxWidth: 400 }}>
                <button className="btn btn-primary form-control" disabled={status === "LOADING"} onClick={() => handleRegister(firebaseUser)}>
                  Register
                </button>
                <button
                  className="btn btn-danger form-control"
                  disabled={status === "LOADING"}
                  onClick={signOut}
                >
                  Sign Out
                </button>
              </div>
            )}

            {firebaseUser && user !== "loading" && user !== null && (
              <div className="d-flex flex-column align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span>{providerLabel(firebaseUser)}</span>
                  <button className="btn btn-sm btn-outline-danger" onClick={signOut}>
                    Sign Out
                  </button>
                </div>
                <AccountLinkPanel
                  firebaseUser={firebaseUser}
                  onLinkWithGoogle={linkWithGoogle}
                  onLinkWithPhone={(u) => linkWithPhone(u, "recaptcha-container-link")}
                  onUnlinkProvider={unlinkProvider}
                />
              </div>
            )}
          </div>
        </div>

        {firebaseUser && user !== "loading" && user !== null && (
          <Outlet context={{ credential: { user: firebaseUser }, user }} />
        )}
      </div>
    </>
  )
}
